import React, { Suspense, useEffect } from "react";

import { Center, Spinner } from "@chakra-ui/react";

import { RedirectToLogin, RequiredAuthProvider } from "@propelauth/react";
import {
  init,
  reactRouterV6BrowserTracingIntegration,
  wrapCreateBrowserRouterV7
} from "@sentry/react";
import "react-datepicker/dist/react-datepicker.css";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from "react-router-dom";
import { Toaster } from "sonner";

import environmentConfig from "@/util/environments";

import { DocumentTitle } from "./components/common/breadcrumbs/DocumentTitle";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import "./index.css";
import setupRoutes from "./routes";
import { AppRoutesContextProvider } from "./routes/context";
import { AxiosInterceptorUpdater } from "./services/api/AxiosInterceptorUpdater";
import setupStore from "./store";
import { getEnvironmentName, isSentryEnabled } from "./util/environments";

// Initialise Sentry when not running locally and not on alpha.
if (isSentryEnabled()) {
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ],
    tracesSampleRate: 0.2,
    environment: getEnvironmentName()
  });
}

const routes = setupRoutes();
const sentryCreateBrowserRouter =
  wrapCreateBrowserRouterV7(createBrowserRouter);
const router = sentryCreateBrowserRouter(routes);
const store = setupStore({ router });

const root = ReactDOM.createRoot(
  document.getElementById("root")! as HTMLElement
);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider>
        <RequiredAuthProvider
          authUrl={environmentConfig.authUrl || ""}
          displayIfLoggedOut={
            <RedirectToLogin
              postLoginRedirectUrl={environmentConfig.baseUrl || "/"}
            />
          }
        >
          <AxiosInterceptorUpdater />
          <AppRoutesContextProvider routes={routes}>
            <DocumentTitle siteTitle="Jack" />
            <Suspense
              fallback={
                <Center>
                  <Spinner />
                </Center>
              }
            >
              <RouterProvider router={router} />
            </Suspense>
          </AppRoutesContextProvider>
        </RequiredAuthProvider>
      </ThemeProvider>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{ style: { marginTop: "50px" } }}
      />
    </Provider>
  </React.StrictMode>
);
